const AREA_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;
const DIMENSION_PATTERN = /^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/;
const FLOAT_TOLERANCE = 0.000001;

export const canonicalSize = (product) =>
  [Number(product.physicalLengthCm), Number(product.physicalWidthCm)]
    .sort((a, b) => a - b)
    .join('x');

export const sizeLabel = (token) => `${token.replace('x', ' × ')} cm`;

export const validRequestedArea = (text) =>
  AREA_PATTERN.test(text) && Number(text) > 0 && Number(text) <= 10000;

const validDimension = (value) =>
  DIMENSION_PATTERN.test(value) && Number(value) > 0 && Number(value) <= 100;

const hundredths = (value) => {
  const [whole, fraction = ''] = value.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
};

export function dimensionsArea(length, width) {
  if (!validDimension(length) || !validDimension(width)) return '';
  return String((hundredths(length) * hundredths(width)) / 10000);
}

const finiteContractNumber = (value) => typeof value === 'number' && Number.isFinite(value);

function validPiece(piece, product, requestedArea, excluded, seen) {
  const length = Number(piece.physicalLengthCm);
  const width = Number(piece.physicalWidthCm);
  const area = Number(piece.areaCm2);

  if (![piece.physicalLengthCm, piece.physicalWidthCm, piece.areaCm2].every(finiteContractNumber)) {
    return false;
  }

  return Boolean(
    product &&
      piece.name === product.name &&
      (product.sku === undefined || piece.sku === product.sku) &&
      !seen.has(piece.productId) &&
      !excluded.includes(canonicalSize(product)) &&
      Number.isInteger(piece.quantity) &&
      piece.quantity >= 1 &&
      piece.quantity <= 1000 &&
      Number.isFinite(length) &&
      Number.isFinite(width) &&
      length > 0 &&
      width > 0 &&
      length === Number(product.physicalLengthCm) &&
      width === Number(product.physicalWidthCm) &&
      piece.sizeToken === product.sizeToken &&
      Number.isFinite(area) &&
      Math.abs(length * width - area) <= FLOAT_TOLERANCE &&
      area <= Number(requestedArea)
  );
}

export function validAreaResult(result, group, requestedArea, excluded, fingerprint) {
  if (
    result?.measurementMode !== 'product_area' ||
    !['match', 'no_fit', 'rule_unavailable'].includes(result.status) ||
    result.catalogFingerprint !== fingerprint
  ) {
    return false;
  }
  if (result.status !== 'match') return true;

  if (
    ![result.requestedAreaCm2, result.totalAreaCm2, result.excessAreaCm2].every(
      finiteContractNumber
    ) ||
    result.requestedAreaCm2 <= 0 ||
    result.totalAreaCm2 <= 0 ||
    result.excessAreaCm2 < 0 ||
    !Array.isArray(result.pieces) ||
    !result.pieces.length ||
    Number(result.requestedAreaCm2) !== Number(requestedArea)
  ) {
    return false;
  }

  let totalArea = 0;
  let pieceCount = 0;
  const seen = new Set();

  for (const piece of result.pieces) {
    const product = group?.products.find((item) => item.productId === piece.productId);
    if (!validPiece(piece, product, requestedArea, excluded, seen)) return false;

    seen.add(piece.productId);
    totalArea += Number(piece.areaCm2) * piece.quantity;
    pieceCount += piece.quantity;
  }

  return (
    pieceCount === result.pieceCount &&
    pieceCount <= 1000 &&
    totalArea + FLOAT_TOLERANCE >= Number(requestedArea) &&
    Math.abs(totalArea - Number(result.totalAreaCm2)) < FLOAT_TOLERANCE &&
    Math.abs(totalArea - Number(requestedArea) - Number(result.excessAreaCm2)) < FLOAT_TOLERANCE
  );
}

export function sortedSizeTokens(sizes, excluded = []) {
  const area = (token) => token.split('x').reduce((total, value) => total * Number(value), 1);
  return [...new Set([...sizes, ...excluded])].sort(
    (left, right) => area(left) - area(right) || left.localeCompare(right)
  );
}
